import React, { useState, useRef, useCallback } from 'react';
import './TestePage.css';

const BASE = 'https://www.jouse.com.br/static/img';

const IMG = {
  logo:           `${BASE}/logo/jouse.svg`,
  logoGray:       `${BASE}/logo/jouse-gray.svg`,
  checkGold:      `${BASE}/common/icon/icon_checkmark-gold.svg`,
  productPreta:   `${BASE}/product/camiseta/preta.jpg`,
  dollarGreen:    `${BASE}/common/icon/icon_dollar_sign-green.svg`,
  truckGreen:     `${BASE}/common/icon/icon_truck-green.svg`,
  hanger:         `${BASE}/common/icon/icon_hanger.svg`,
  iconLeaf:       `${BASE}/common/icon/icon_leaf.svg`,
  iconTshirt:     `${BASE}/common/icon/icon_tshirt.svg`,
  iconUnwrinkled: `${BASE}/common/icon/icon_unwrinkled_fabric.svg`,
  iconWind:       `${BASE}/common/icon/icon_wind.svg`,
  iconMagnify:    `${BASE}/common/icon/icon_magnifying_glass.svg`,
  iconQuote:      `${BASE}/common/icon/icon_quotation_mark.svg`,
  iconQuestion:   `${BASE}/common/icon/icon_question_mark.svg`,
  iconPlus:       `${BASE}/common/icon/icon_plus_sign-gray.svg`,
  cardBrands:     `${BASE}/common/card_brands.svg`,

  leafPres:       `${BASE}/presentation/camiseta/leaf_icon-2x.png`,
  camisetaPres:   `${BASE}/presentation/camiseta/camiseta.png`,
  tshirtIcon:     `${BASE}/presentation/camiseta/t_shirt_icon-2x.png`,
  unwrinkledIcon: `${BASE}/presentation/camiseta/unwrinkled_fabric_icon-2x.png`,
  windIcon:       `${BASE}/presentation/camiseta/wind_icon-2x.png`,
  measureIcon:    `${BASE}/presentation/camiseta/t_shirt_measurements_icon-2x.png`,

  bg1: `${BASE}/presentation/camiseta/background_1.jpg`,
  bg2: `${BASE}/presentation/camiseta/background_2.jpg`,
  bg3: `${BASE}/presentation/camiseta/background_3.jpg`,
  bg4: `${BASE}/presentation/camiseta/background_4.jpg`,
  bg5: `${BASE}/presentation/camiseta/background_5.jpg`,
};

const PRODUCT_SLIDES = [
  { src: '/img/produto/slide-1.jpg',  alt: 'Produto - modelo usando colar' },
  { src: '/img/produto/slide-3.webp', alt: 'Produto - colar Salvatore pendente' },
  { src: '/img/produto/slide-2.webp', alt: 'Produto - corrente detalhe' },
];

function ProductCarousel() {
  const [current, setCurrent]   = useState(0);
  const trackRef                = useRef(null);
  const dragStart               = useRef(null);
  const dragging                = useRef(false);

  const goTo = useCallback((idx) => {
    const total = PRODUCT_SLIDES.length;
    setCurrent((idx + total) % total);
  }, []);

  const onPointerDown = (e) => {
    dragStart.current = e.clientX;
    dragging.current  = false;
  };

  const onPointerMove = (e) => {
    if (dragStart.current !== null && Math.abs(e.clientX - dragStart.current) > 5) {
      dragging.current = true;
    }
  };

  const onPointerUp = (e) => {
    if (dragStart.current === null) return;
    const delta = e.clientX - dragStart.current;
    if (Math.abs(delta) > 40) goTo(current + (delta < 0 ? 1 : -1));
    dragStart.current = null;
    dragging.current  = false;
  };

  /* touch */
  const touchStart = useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 40) goTo(current + (delta < 0 ? 1 : -1));
    touchStart.current = null;
  };

  return (
    <div className="pc-root">
      <div
        className="pc-viewport"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => { dragStart.current = null; }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="pc-track"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {PRODUCT_SLIDES.map((s, i) => (
            <div key={i} className="pc-slide">
              <img src={s.src} alt={s.alt} draggable={false} />
            </div>
          ))}
        </div>

        {/* Arrows */}
        <button className="pc-arrow pc-arrow--prev" onClick={() => goTo(current - 1)} aria-label="Anterior">
          ‹
        </button>
        <button className="pc-arrow pc-arrow--next" onClick={() => goTo(current + 1)} aria-label="Próxima">
          ›
        </button>
      </div>

      {/* Dots */}
      <div className="pc-dots">
        {PRODUCT_SLIDES.map((_, i) => (
          <button
            key={i}
            className={`pc-dot${i === current ? ' active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Imagem ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const COLORS = [
  { name: 'preta',  label: 'Preta',         gradient: 'linear-gradient(rgb(72,72,72), rgb(7,7,7))' },
  { name: 'navy',   label: 'Azul Marinho',   gradient: 'linear-gradient(rgb(56,78,118), rgb(24,40,67))' },
  { name: 'verde',  label: 'Verde Militar',  gradient: 'linear-gradient(rgb(117,132,92), rgb(64,73,49))' },
  { name: 'cinza',  label: 'Cinza Mescla',   gradient: 'linear-gradient(rgb(170,170,170), rgb(102,102,102))' },
  { name: 'areia',  label: 'Areia',          gradient: 'linear-gradient(rgb(221,210,193), rgb(187,174,154))' },
];

const SIZES = ['P', 'M', 'G', 'GG', '3G'];

const COMFORT_BARS = [
  { name: 'Tecido de celulose', level: 'Ultra confortável', pct: 98,  green: true },
  { name: 'Algodão egípcio',    level: 'Confortável',       pct: 30,  green: false },
  { name: 'Algodão pima',       level: 'Confortável',       pct: 25,  green: false },
  { name: 'Algodão',            level: 'Razoável',          pct: 15,  green: false },
];

const STEPS = [
  { bg: IMG.bg1, step: 'Etapa 1', label: 'Florestas sustentáveis' },
  { bg: IMG.bg2, step: 'Etapa 2', label: 'Madeira certificada' },
  { bg: IMG.bg3, step: 'Etapa 3', label: 'Polpa de celulose' },
  { bg: IMG.bg4, step: 'Etapa 4', label: 'Fibra de celulose' },
  { bg: IMG.bg5, step: 'Etapa 5', label: 'Tecido de celulose' },
];

const FEATURE_BLOCKS = [
  {
    icon: IMG.tshirtIcon,
    title: 'Nunca desbota.',
    sub: 'Está sempre parecendo nova.',
    text: 'A camiseta da Jouse preserva sua cor original e continua parecendo nova por muito tempo, sem desbotar e sem perder a intensidade da sua cor. Isto é possível porque o tingimento do tecido de celulose é capaz de resistir a ciclos de lavagens muito mais longos do que o algodão.',
  },
  {
    icon: IMG.unwrinkledIcon,
    title: 'Não precisa passar.',
    sub: 'É só lavar, secar e usar.',
    text: 'A camiseta da Jouse não precisa ser passada após cada lavagem e também pode ser usada ao longo de um dia inteiro sem amassado. Isso é possível porque o tecido de celulose tem uma resistência natural a rugas, ao contrário dos tecidos de algodão que amassam com facilidade.',
  },
  {
    icon: IMG.windIcon,
    title: 'Proteção antiodor.',
    sub: 'Esqueça o mau cheiro.',
    text: 'O tecido de celulose é muito mais eficiente que o algodão em diminuir a proliferação das bactérias que causam o mau cheiro. Com a camiseta da Jouse, você pode usá-la por mais tempo entre as lavagens sem precisar se preocupar com o cheiro.',
  },
  {
    icon: IMG.measureIcon,
    title: 'Um caimento perfeito.',
    sub: 'Que valoriza seu corpo.',
    text: 'A camiseta da Jouse tem um caimento perfeito que valoriza seu corpo. Sua modelagem não marca a barriga e tem o comprimento ideal para usar por dentro ou por fora da calça.',
  },
];

const TESTIMONIALS = [
  { user: 'danielferreiraimob', text: 'Usei pela primeira vez hoje e é excelente!!! Vou comprar mais com certeza. Melhor camiseta que comprei na vida.', img: `${BASE}/product/comments/danielferreiraimob.jpg` },
  { user: 'gelson_barbosa_',    text: 'Sem dúvidas a camiseta mais confortável que já vesti na vida.',                                                    img: `${BASE}/product/comments/gelson_barbosa_.jpg` },
  { user: 'feliperebs',         text: 'Tenho a carteira da Jouse há 5 anos e está intacta, agora vi que lançaram a camiseta, comprei e achei incrível.',  img: `${BASE}/product/comments/feliperebs.jpg` },
  { user: 'zimerman_sergio',    text: 'Camiseta excelente. Veste bem e muito confortável',                                                                 img: `${BASE}/product/comments/zimerman_sergio.jpg`, verified: true },
  { user: 'alexandre.bersan',   text: 'Eu já comprei. E é muito melhor do que parece. Estou querendo comprar mais umas 4 e passar a vestir apenas isso.',  img: `${BASE}/product/comments/alexandre.bersan.jpg` },
  { user: 'gilmarlourenco90',   text: 'A poucos dias comprei duas e estou impressionado com o conforto e qualidade do tecido 🙌',                          img: `${BASE}/product/comments/gilmarlourenco90.jpg` },
  { user: 'andreivedovato',     text: 'A camiseta é simplesmente sensacional. Já tenho duas e pretendo adquirir mais muito em breve. Custo benefício excelente.', img: `${BASE}/product/comments/andreivedovato.jpg` },
];

const SIZE_TABLE = [
  { size: 'P',  width: '51 cm', length: '68 cm' },
  { size: 'M',  width: '53 cm', length: '70 cm' },
  { size: 'G',  width: '55 cm', length: '72 cm' },
  { size: 'GG', width: '57 cm', length: '74 cm' },
  { size: '3G', width: '61 cm', length: '76 cm' },
];

const FAQS = [
  {
    q: 'Qual tamanho devo escolher?',
    a: 'A modelagem da Jouse fica excelente na imensa maioria dos corpos, por isso, você pode escolher o tamanho que costuma usar normalmente. Se precisar de uma sugestão, você pode usar o provador virtual da Jouse para descobrir qual é o tamanho perfeito para você.',
  },
  { q: 'Quais são as medidas da camiseta?', a: null },
  {
    q: 'Qual é a composição da camiseta?',
    a: 'A camiseta da Jouse é composta por 95% de MicroModal™ e 5% de elastano. O MicroModal™ é uma fibra ultra macia produzida com a celulose de árvores colhidas na Europa. Por ser uma evolução do modal genérico e do TENCEL™ Modal usado por outras marcas, as fibras do MicroModal™ são significativamente mais macias e finas.',
  },
  {
    q: 'Se não servir, posso trocar ou devolver?',
    a: 'Com certeza, pode ficar tranquilo. O processo de troca ou devolução da Jouse é fácil, rápido e totalmente gratuito.',
  },
];

export default function TestePage() {
  const [selectedColor, setSelectedColor] = useState('preta');
  const [selectedSize,  setSelectedSize]  = useState(null);
  const [quantity,      setQuantity]      = useState(1);
  const [openSection,   setOpenSection]   = useState(null);
  const [openFaq,       setOpenFaq]       = useState(null);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [showAll,       setShowAll]       = useState(false);

  const price = quantity >= 3 ? 129 : 149;
  const colorLabel = COLORS.find(c => c.name === selectedColor)?.label ?? '';

  const toggleSection = (id) => setOpenSection(prev => (prev === id ? null : id));
  const toggleFaq     = (i)  => setOpenFaq(prev => (prev === i ? null : i));

  return (
    <div className="jouse-page">

      {/* ── Header ── */}
      <header className="jouse-header">
        <button className="jouse-header__logo-btn" onClick={() => setMenuOpen(o => !o)}>
          <img src={IMG.logo} alt="Logo Jouse" className="jouse-header__logo" />
        </button>
        <button className="jouse-header__menu-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </header>

      {/* ── Nav drawer ── */}
      {menuOpen && (
        <nav className="jouse-nav">
          <ul>
            {[
              { href: '/camiseta/',      name: 'Camiseta',     sub: 'Camiseta de celulose' },
              { href: '/jouse-polo/',    name: 'Jouse Polo',   sub: 'Camisa polo' },
              { href: '/jouse-chino/',   name: 'Jouse Chino',  sub: 'Calça chino' },
              { href: '/jouse-legacy/',  name: 'Jouse Legacy', sub: 'Jaqueta de couro' },
              { href: '/jouse-serie-1/', name: 'Jouse Série 1',sub: 'Carteira de couro' },
            ].map(item => (
              <li key={item.href}>
                <a href={item.href}>{item.name}<small>{item.sub}</small></a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <main className="jouse-main">

        {/* ── Hero ── */}
        <div className="jouse-hero">

          {/* Left – image carousel */}
          <div className="jouse-hero__image-col">
            <div className="jouse-badge">
              <img src={IMG.checkGold} alt="" />
              <div>
                <strong>Garantia total de satisfação</strong>
                <span>Troca ou devolução 100% grátis</span>
              </div>
            </div>
            <ProductCarousel />
          </div>

          {/* Right – configurator */}
          <div className="jouse-hero__config-col">
            <h1 className="jouse-hero__title">Selecione sua camiseta</h1>
            <hr className="jouse-divider" />

            {/* Color */}
            <div className="jouse-selector">
              <p className="jouse-selector__label">Cor</p>
              <span className="jouse-selector__value">{colorLabel}</span>
              <div className="jouse-color-swatches">
                {COLORS.map(c => (
                  <button
                    key={c.name}
                    className={`jouse-color-swatch${selectedColor === c.name ? ' active' : ''}`}
                    style={{ background: c.gradient }}
                    onClick={() => setSelectedColor(c.name)}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="jouse-selector">
              <p className="jouse-selector__label">Tamanho</p>
              <span className="jouse-selector__value">
                {selectedSize ? '' : 'Selecione um tamanho'}
              </span>
              <div className="jouse-size-buttons">
                {SIZES.map(s => (
                  <button
                    key={s}
                    className={`jouse-size-btn${selectedSize === s ? ' active' : ''}`}
                    onClick={() => setSelectedSize(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="jouse-selector">
              <p className="jouse-selector__label">Quantidade</p>
              <p className="jouse-qty-label">{quantity} unidade{quantity !== 1 ? 's' : ''}</p>
              <div className="jouse-qty-control">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>–</button>
                <input type="number" value={quantity} readOnly />
                <button onClick={() => setQuantity(q => q + 1)}>+</button>
              </div>
            </div>

            {/* Price */}
            <div className="jouse-price">
              <span className="jouse-price__amount">R$ {price}</span>
              <span className="jouse-price__installment"> em até 3x sem juros</span>
            </div>

            {/* Bulk promo */}
            <div className="jouse-promo">
              <img src={IMG.dollarGreen} alt="" />
              <div>
                <p className="jouse-promo__title">Leve 3 ou mais por R$ 129 cada</p>
                <p className="jouse-promo__sub">Desconto aplicado automaticamente</p>
              </div>
            </div>

            {/* Shipping */}
            <div className="jouse-shipping">
              <img src={IMG.truckGreen} alt="" />
              <div>
                <p className="jouse-shipping__title">Frete rápido e barato</p>
                <p className="jouse-shipping__sub">
                  Entrega a partir de <strong>2 dias úteis</strong>
                </p>
                <p className="jouse-shipping__sub">para Sul, Sudeste e Centro-Oeste</p>
              </div>
            </div>

            <button className="jouse-cta-btn">Selecionar</button>

            {/* Virtual fitting */}
            <div className="jouse-fitting">
              <img src={IMG.hanger} alt="" />
              <div>
                <p>Provador virtual</p>
                <button className="jouse-fitting__link">Descubra seu tamanho ›</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Features bar ── */}
        <div className="jouse-features">
          {[
            { icon: IMG.iconLeaf,       title: 'Ultra confortável',    sub: 'Super macia e suave' },
            { icon: IMG.iconTshirt,     title: 'Nunca desbota',        sub: 'Parece nova para sempre' },
            { icon: IMG.iconUnwrinkled, title: 'Não precisa passar',   sub: 'É só lavar, secar e usar' },
            { icon: IMG.iconWind,       title: 'Proteção antiodor',    sub: 'Esqueça o mau cheiro' },
          ].map(f => (
            <div key={f.title} className="jouse-feature-item">
              <img src={f.icon} alt="" />
              <p>{f.title}</p>
              <small>{f.sub}</small>
            </div>
          ))}
        </div>

        {/* ── Accordion: Introdução ── */}
        <div className="jouse-accordion">
          <button className="jouse-accordion__header" onClick={() => toggleSection('intro')}>
            <span className="jouse-accordion__header-left">
              <img src={IMG.iconMagnify} alt="" />
              <span>
                <strong>Introdução</strong>
                <small>Conheça a camiseta da Jouse</small>
              </span>
            </span>
            {openSection === 'intro'
              ? <span className="jouse-accordion__close">×</span>
              : <img src={IMG.iconPlus} alt="" className="jouse-accordion__icon" />}
          </button>

          {openSection === 'intro' && (
            <div className="jouse-accordion__content">

              {/* Intro text */}
              <div className="jouse-intro-section">
                <img src={IMG.leafPres} alt="" className="jouse-intro-leaf" />
                <h2>
                  Conheça a nova<br />
                  camiseta de <span className="jouse-green">celulose</span>.
                </h2>
                <h3>É só tocar para se apaixonar.</h3>
                <p>
                  A camiseta da Jouse é feita de celulose de árvores colhidas na Europa.
                  Seu novo tecido é incrivelmente macio e muito mais confortável do que
                  qualquer outro tecido do mundo. Depois de provar a camiseta da Jouse,
                  você nunca mais vai querer voltar a usar camisetas comuns de algodão.
                </p>
              </div>

              <div className="jouse-intro-img-wrapper">
                <img src={IMG.camisetaPres} alt="Camiseta da Jouse" className="jouse-intro-img" />
              </div>

              {/* Comfort chart */}
              <div className="jouse-comfort">
                <h3>Nível de conforto</h3>
                <hr />
                {COMFORT_BARS.map(bar => (
                  <div key={bar.name} className="jouse-comfort-item">
                    <p className={bar.green ? 'jouse-green' : ''}>{bar.name}</p>
                    <small>{bar.level}</small>
                    <div className="jouse-comfort-bar">
                      <div
                        className={`jouse-comfort-bar__fill ${bar.green ? 'green' : 'gray'}`}
                        style={{ width: `${bar.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Process steps */}
              <div className="jouse-steps">
                {STEPS.map(s => (
                  <div
                    key={s.step}
                    className="jouse-step"
                    style={{ backgroundImage: `url(${s.bg})` }}
                  >
                    <small>{s.step}</small>
                    <p>{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Feature blocks */}
              {FEATURE_BLOCKS.map(b => (
                <div key={b.title} className="jouse-feature-block">
                  <img src={b.icon} alt="" />
                  <h2>{b.title}</h2>
                  <h3>{b.sub}</h3>
                  <p>{b.text}</p>
                </div>
              ))}

              <div className="jouse-feature-block">
                <h2>Use todos os dias.</h2>
                <h3>Em qualquer ocasião.</h3>
              </div>
            </div>
          )}
        </div>

        {/* ── Accordion: Comentários ── */}
        <div className="jouse-accordion">
          <button className="jouse-accordion__header" onClick={() => toggleSection('comments')}>
            <span className="jouse-accordion__header-left">
              <img src={IMG.iconQuote} alt="" />
              <span>
                <strong>Comentários dos usuários</strong>
                <small>Publicados no Instagram</small>
              </span>
            </span>
            {openSection === 'comments'
              ? <span className="jouse-accordion__close">×</span>
              : <img src={IMG.iconPlus} alt="" className="jouse-accordion__icon" />}
          </button>

          {openSection === 'comments' && (
            <div className="jouse-accordion__content jouse-testimonials">
              {TESTIMONIALS.slice(0, showAll ? 7 : 4).map(t => (
                <div key={t.user} className="jouse-testimonial">
                  <div className="jouse-testimonial__header">
                    <img src={t.img} alt={t.user} className="jouse-testimonial__avatar" />
                    <div>
                      <p className="jouse-testimonial__username">{t.user}</p>
                      {t.verified && <span className="jouse-testimonial__verified">✓</span>}
                    </div>
                  </div>
                  <p className="jouse-testimonial__text">{t.text}</p>
                </div>
              ))}
              {!showAll && (
                <button className="jouse-show-more" onClick={() => setShowAll(true)}>
                  Exibir mais comentários
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Accordion: FAQ ── */}
        <div className="jouse-accordion">
          <button className="jouse-accordion__header" onClick={() => toggleSection('faq')}>
            <span className="jouse-accordion__header-left">
              <img src={IMG.iconQuestion} alt="" />
              <span>
                <strong>Perguntas frequentes</strong>
                <small>Sua dúvida pode estar aqui</small>
              </span>
            </span>
            {openSection === 'faq'
              ? <span className="jouse-accordion__close">×</span>
              : <img src={IMG.iconPlus} alt="" className="jouse-accordion__icon" />}
          </button>

          {openSection === 'faq' && (
            <div className="jouse-accordion__content">
              {FAQS.map((faq, i) => (
                <div key={i} className="jouse-faq-item">
                  <button className="jouse-faq-item__q" onClick={() => toggleFaq(i)}>
                    {faq.q}
                  </button>
                  {openFaq === i && (
                    <div className="jouse-faq-item__a">
                      {i === 1 ? (
                        <>
                          <p>
                            Usar o provador virtual da Jouse é a forma mais fácil e rápida de descobrir
                            qual é o tamanho perfeito para você. De qualquer forma, se você também quiser
                            checar as medidas de cada tamanho, aqui está a tabela de medidas:
                          </p>
                          <table className="jouse-size-table">
                            <thead>
                              <tr><th></th><th>Largura</th><th>Comprimento</th></tr>
                            </thead>
                            <tbody>
                              {SIZE_TABLE.map(row => (
                                <tr key={row.size}>
                                  <td><strong>{row.size}</strong></td>
                                  <td>{row.width}</td>
                                  <td>{row.length}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="jouse-size-note">
                            As medidas consideram a camiseta esticada em uma superfície plana e podem
                            apresentar pequenas variações.
                          </p>
                        </>
                      ) : (
                        <p>{faq.a}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div className="jouse-help">
                <p>Precisa de ajuda?</p>
                <small>Sempre estaremos aqui para te ajudar.</small>
                <a href="/contato/">Fale conosco ›</a>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="jouse-footer">
        <div className="jouse-footer__grid">
          <div>
            <h4>Suporte</h4>
            <ul>
              <li><a href="/acompanhar-pedido/">Acompanhar pedido</a></li>
              <li><a href="/trocas-e-devolucoes/">Trocas e devoluções</a></li>
              <li><a href="/contato/">Fale conosco</a></li>
            </ul>
          </div>
          <div>
            <h4>Institucional</h4>
            <ul>
              <li><a href="/sobre/">Sobre a Jouse</a></li>
              <li><a href="/sobre/#sustentabilidade">Sustentabilidade</a></li>
              <li><a href="/sobre/#iniciativas-sociais">Iniciativas sociais</a></li>
            </ul>
          </div>
          <div>
            <h4>Políticas</h4>
            <ul>
              <li><a href="/politica-de-vendas/">Política de vendas</a></li>
              <li><a href="/politica-de-privacidade/">Política de privacidade</a></li>
            </ul>
          </div>
        </div>

        <div className="jouse-footer__social">
          <a href="https://www.instagram.com/jouse/" target="_blank" rel="noreferrer">Instagram</a>
          <a href="https://www.facebook.com/jouse/" target="_blank" rel="noreferrer">Facebook</a>
          <a href="https://www.linkedin.com/company/jouse/" target="_blank" rel="noreferrer">LinkedIn</a>
          <a href="https://www.youtube.com/@JouseBrasil" target="_blank" rel="noreferrer">YouTube</a>
        </div>

        <div className="jouse-footer__bottom">
          <img src={IMG.logoGray} alt="Logo Jouse" />
          <p>Copyright © 2026 Jouse Ltda. CNPJ: 31.758.172/0001-52</p>
          <p>Rua Carlos Dietzsch - Curitiba, PR 80330-000</p>
          <img src={IMG.cardBrands} alt="Bandeiras de cartões de crédito" />
        </div>
      </footer>
    </div>
  );
}
